(() => {
  const imageCache = new Map();
  const metal = {
    silver: ['#343b40', '#eaf0f1', '#a7b0b4', '#ffffff', '#6e777d'],
    'yellow-gold': ['#654218', '#fff0ae', '#c59435', '#fff4b9', '#8a5c1f'],
    'rose-gold': ['#704b3e', '#ffe1c6', '#ba7c65', '#fff0de', '#8f594b'],
    'two-tone': ['#343b40', '#eaf0f1', '#a7b0b4', '#ffffff', '#6e777d'],
  };

  function imageFor(url) {
    if (!url) return Promise.reject(new Error('A photo-reference asset is missing.'));
    if (!imageCache.has(url)) {
      imageCache.set(url, new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error(`Could not load reference asset: ${url}`));
        image.src = url;
      }));
    }
    return imageCache.get(url);
  }

  function selected(manifest, selections, role) {
    return manifest.layers.find((layer) =>
      layer.role === role && selections.get(layer.questionId) === layer.answerId
    );
  }

  function drawDial(context, sheet, crop, dial) {
    const [sourceX, sourceY, sourceRadius] = crop;
    context.save();
    context.beginPath();
    context.arc(dial.x, dial.y, dial.radius, 0, Math.PI * 2);
    context.clip();
    context.drawImage(
      sheet,
      sourceX - sourceRadius,
      sourceY - sourceRadius,
      sourceRadius * 2,
      sourceRadius * 2,
      dial.x - dial.radius,
      dial.y - dial.radius,
      dial.radius * 2,
      dial.radius * 2
    );
    context.restore();
  }

  function sourcePointOnDial(source, crop, dial) {
    const scale = dial.radius / crop[2];
    return {
      x: dial.x + (source[0] - crop[0]) * scale,
      y: dial.y + (source[1] - crop[1]) * scale,
    };
  }

  function dateWindowOnDial(dialOption, composition) {
    if (!dialOption?.dateWindow) return null;
    const [left, top, width, height] = dialOption.dateWindow;
    const scale = composition.dial.radius / dialOption.crop[2];
    return {
      ...sourcePointOnDial([left + width / 2, top + height / 2], dialOption.crop, composition.dial),
      width: width * scale,
      height: height * scale,
    };
  }

  function drawDateWindowBacking(context, composition, dialOption) {
    const aperture = dateWindowOnDial(dialOption, composition);
    if (!aperture || dialOption.dateWindowOpaque) return;
    // The source dial's alpha cutout masks the wheel, preserving its metal rim.
    context.save();
    context.fillStyle = '#f8f7f2';
    context.fillRect(aperture.x - aperture.width / 2 - 1, aperture.y - aperture.height / 2 - 1, aperture.width + 2, aperture.height + 2);
    context.restore();
  }

  function drawWindowDate(context, composition, dialOption, magnified) {
    const aperture = dateWindowOnDial(dialOption, composition);
    const label = dialOption.dateText || composition.date.text;
    context.save();
    context.beginPath();
    context.rect(aperture.x - aperture.width / 2 + 0.2, aperture.y - aperture.height / 2 + 0.2, aperture.width - 0.4, aperture.height - 0.4);
    context.clip();
    if (dialOption.dateWindowOpaque) {
      context.fillStyle = '#f8f7f2';
      context.fillRect(aperture.x - aperture.width / 2, aperture.y - aperture.height / 2, aperture.width, aperture.height);
    }
    context.fillStyle = '#242625';
    context.textAlign = 'left';
    context.textBaseline = 'alphabetic';
    let fontSize = aperture.height * (magnified ? 1.03 : 0.84);
    context.font = `500 ${fontSize}px Arial, sans-serif`;
    let metrics = context.measureText(label);
    const inkWidth = metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight;
    const inkHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
    fontSize *= Math.min(1, (aperture.width - 6) / inkWidth, (aperture.height - 5) / inkHeight);
    context.font = `500 ${fontSize}px Arial, sans-serif`;
    metrics = context.measureText(label);
    // Center the visible glyphs, not the font's baseline or advance box.
    context.fillText(label,
      aperture.x + (metrics.actualBoundingBoxLeft - metrics.actualBoundingBoxRight) / 2,
      aperture.y + (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2);
    context.restore();
  }

  function drawCalendar(context, composition, dialOption, magnified) {
    const date = composition.date;
    if (date && dialOption.dateWindow) {
      drawWindowDate(context, composition, dialOption, magnified);
    } else if (date) {
      const fitted = Boolean(dialOption.dateSource);
      const nativeDate = dialOption.dateSource
        ? sourcePointOnDial(dialOption.dateSource, dialOption.crop, composition.dial)
        : date;
      const x = magnified ? date.x : nativeDate.x;
      const y = magnified ? date.y : nativeDate.y;
      context.save();
      if (fitted && magnified) {
        // The date wheel is part of the dial, beneath either style of crystal.
        // Fill the whole lens opening; the original dial's smaller blank date
        // square must not leave a dark strip beside the numeral.
        context.fillStyle = '#f8f7f2';
        context.fillRect(x - 35, y - 26, 70, 52);
      } else if (!fitted && composition.magnifier === 'drawn') {
        context.fillStyle = '#f6f5f0';
        context.fillRect(date.x - 25, date.y - 21, 50, 42);
      }
      if (fitted) {
        context.beginPath();
        context.rect(x - (magnified ? 33 : 22), y - (magnified ? 24 : 18), magnified ? 66 : 44, magnified ? 48 : 36);
        context.clip();
      }
      context.fillStyle = '#242625';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = `500 ${fitted ? (magnified ? 34 : 29) : (magnified && composition.magnifier === 'drawn' ? 35 : 29)}px Arial, sans-serif`;
      context.fillText(dialOption.dateText || date.text, x, y + 1, fitted ? (magnified ? 62 : 40) : undefined);
      context.restore();
    }
    if (composition.day) {
      const x = dialOption.dayX ?? composition.day.x;
      const y = dialOption.dayY ?? composition.day.y;
      const width = dialOption.dayWidth || 150;
      const label = dialOption.dayText || composition.day.text;
      context.save();
      context.beginPath();
      context.rect(x - width / 2, y - 16, width, 32);
      context.clip();
      context.fillStyle = dialOption.dayColor || '#232726';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = `600 ${dialOption.dayFontSize || 20}px Arial, sans-serif`;
      const letters = [...label];
      const advances = letters.map((letter) => context.measureText(letter).width + 0.3);
      const fullWidth = advances.reduce((sum, advance) => sum + advance, 0);
      const compression = Math.min(1, (width - 10) / fullWidth);
      const arcRadius = dialOption.dayArcRadius || 250;
      let offset = -fullWidth / 2;
      letters.forEach((letter, index) => {
        const localX = (offset + advances[index] / 2) * compression;
        const localY = arcRadius - Math.sqrt(arcRadius * arcRadius - localX * localX);
        context.save();
        context.translate(x + localX, y + 1 + localY);
        context.rotate(Math.asin(localX / arcRadius));
        context.fillText(letter, 0, 0);
        context.restore();
        offset += advances[index];
      });
      context.restore();
    }
  }

  function drawHand(context, x, y, angle, length, width, finish, luminous) {
    const shades = metal[finish] || metal.silver;
    context.save();
    context.translate(x, y);
    context.rotate(angle);
    context.shadowColor = 'rgba(0, 0, 0, 0.36)';
    context.shadowBlur = 7;
    context.shadowOffsetY = 4;
    context.beginPath();
    context.moveTo(-25, -width * 0.38);
    context.lineTo(length * 0.72, -width * 0.5);
    context.lineTo(length, 0);
    context.lineTo(length * 0.72, width * 0.5);
    context.lineTo(-25, width * 0.38);
    context.closePath();
    const gradient = context.createLinearGradient(0, -width / 2, 0, width / 2);
    shades.forEach((shade, index) => gradient.addColorStop(index / (shades.length - 1), shade));
    context.fillStyle = gradient;
    context.fill();
    context.shadowColor = 'transparent';
    context.lineWidth = 2;
    context.strokeStyle = shades[0];
    context.stroke();
    if (luminous) {
      context.beginPath();
      context.moveTo(21, -width * 0.18);
      context.lineTo(length * 0.71, -width * 0.2);
      context.lineTo(length * 0.8, 0);
      context.lineTo(length * 0.71, width * 0.2);
      context.lineTo(21, width * 0.18);
      context.closePath();
      context.fillStyle = '#f3f3e9';
      context.fill();
    }
    context.restore();
  }

  function drawHands(context, dial, finish) {
    drawHand(context, dial.x, dial.y, -2.51, dial.radius * 0.58, 27, finish, true);
    drawHand(context, dial.x, dial.y, -0.52, dial.radius * 0.78, 19, finish, true);
    const shades = metal[finish] || metal.silver;
    context.beginPath();
    context.arc(dial.x, dial.y, 13, 0, Math.PI * 2);
    context.fillStyle = shades[2];
    context.fill();
    context.strokeStyle = shades[0];
    context.lineWidth = 2;
    context.stroke();
  }

  function drawSecondHand(context, dial, finish) {
    const shades = metal[finish] || metal.silver;
    context.save();
    context.translate(dial.x, dial.y);
    context.rotate(1.86);
    context.shadowColor = 'rgba(0, 0, 0, 0.3)';
    context.shadowBlur = 4;
    context.shadowOffsetY = 3;
    context.beginPath();
    context.moveTo(-54, -2);
    context.lineTo(dial.radius * 0.83, -3);
    context.lineTo(dial.radius * 0.92, 0);
    context.lineTo(dial.radius * 0.83, 3);
    context.lineTo(-54, 2);
    context.closePath();
    context.fillStyle = shades[2];
    context.fill();
    context.shadowColor = 'transparent';
    context.beginPath();
    context.arc(0, 0, 9, 0, Math.PI * 2);
    context.fillStyle = shades[3];
    context.fill();
    context.strokeStyle = shades[0];
    context.lineWidth = 3;
    context.stroke();
    context.restore();
  }

  function drawLandMagnifier(context, composition, dialOption) {
    const aperture = dateWindowOnDial(dialOption, composition);
    const { x, y } = aperture || composition.date;
    const left = x - 42;
    const top = y - (aperture ? 32.5 : 32);
    context.save();
    context.shadowColor = 'rgba(20, 27, 31, 0.45)';
    context.shadowBlur = 13;
    context.shadowOffsetX = 4;
    context.beginPath();
    context.roundRect(left, top, 84, 65, 13);
    context.fillStyle = 'rgba(255, 255, 255, 0.11)';
    context.fill();
    context.shadowColor = 'transparent';
    const rim = context.createLinearGradient(left, top, left + 84, top + 65);
    rim.addColorStop(0, 'rgba(255, 255, 255, 0.92)');
    rim.addColorStop(0.38, 'rgba(140, 152, 157, 0.46)');
    rim.addColorStop(0.73, 'rgba(255, 255, 255, 0.8)');
    rim.addColorStop(1, 'rgba(62, 69, 74, 0.68)');
    context.strokeStyle = rim;
    context.lineWidth = 7;
    context.stroke();
    context.beginPath();
    context.roundRect(left + 6, top + 5, 72, 55, 9);
    context.strokeStyle = 'rgba(255, 255, 255, 0.48)';
    context.lineWidth = 2;
    context.stroke();
    context.restore();
  }

  function drawAnchoredAsset(context, image, dial, anchor, scale = 1) {
    const width = dial.radius * 2 * scale;
    const factor = width / image.width;
    context.drawImage(
      image,
      dial.x - anchor[0] * factor,
      dial.y - anchor[1] * factor,
      width,
      image.height * factor
    );
  }

  async function render(manifest, selections, assetUrls) {
    const composition = manifest.composition;
    const dial = selected(manifest, selections, 'dial');
    const caseOption = selected(manifest, selections, 'case') || selected(manifest, selections, 'body');
    const braceletOption = selected(manifest, selections, 'bracelet');
    const hands = selected(manifest, selections, 'hands');
    const seconds = selected(manifest, selections, 'seconds');
    const magnifier = selected(manifest, selections, 'magnifier');
    if (!dial || !caseOption || !hands || !seconds || !magnifier) {
      throw new Error('The selected watch composition is incomplete.');
    }
    const lensKey = magnifier.enabled ? 'with' : 'without';
    const caseAsset = (composition.caseAssets || composition.bodyAssets)[caseOption.finish]?.[lensKey];
    const braceletAsset = braceletOption && composition.braceletAssets?.[braceletOption.finish]?.[lensKey];
    const [dialImage, caseImage, braceletImage, handImage, secondImage] = await Promise.all([
      imageFor(assetUrls[dial.themeAsset]),
      imageFor(assetUrls[caseAsset]),
      braceletAsset ? imageFor(assetUrls[braceletAsset]) : Promise.resolve(null),
      imageFor(assetUrls[hands.themeAsset]),
      imageFor(assetUrls[seconds.themeAsset]),
    ]);

    const canvas = document.createElement('canvas');
    canvas.width = composition.size;
    canvas.height = composition.size;
    canvas.className = 'reglage-configurator__composite';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'Assembled watch preview');
    const context = canvas.getContext('2d', { alpha: true });
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    drawDateWindowBacking(context, composition, dial);
    drawDial(context, dialImage, dial.crop, composition.dial);
    drawCalendar(context, composition, dial, magnifier.enabled);
    if (braceletImage) context.drawImage(braceletImage, 0, 0, composition.size, composition.size);
    context.drawImage(caseImage, 0, 0, composition.size, composition.size);
    const dialBox = composition.dial;
    drawAnchoredAsset(context, handImage, dialBox, composition.handsAnchor);
    drawAnchoredAsset(context, secondImage, dialBox, composition.secondsAnchor, composition.secondsScale);
    if (composition.magnifier === 'drawn' && magnifier.enabled) {
      drawLandMagnifier(context, composition, dial);
    }
    return canvas;
  }

  function simplePreview(role, option) {
    const shade = (metal[option.finish] || metal.silver)[2];
    const artwork = role === 'magnifier'
      ? `<rect x="48" y="46" width="64" height="65" rx="12" fill="#fff" stroke="#8b9396" stroke-width="${option.enabled ? 8 : 2}"/><text x="80" y="91" font-family="Arial" font-size="34" text-anchor="middle" fill="#292c2e">8</text>${option.enabled ? '' : '<path d="M38 122 122 38" stroke="#9b463a" stroke-width="6"/>'}`
      : role === 'hands'
        ? `<path d="M80 80 32 48 M80 80 130 42" stroke="#25292b" stroke-width="15" stroke-linecap="round"/><path d="M80 80 32 48 M80 80 130 42" stroke="${shade}" stroke-width="11" stroke-linecap="round"/><circle cx="80" cy="80" r="8" fill="${shade}"/>`
        : `<path d="M80 31 80 126" stroke="#25292b" stroke-width="9"/><path d="M80 31 80 126" stroke="${shade}" stroke-width="5"/><circle cx="80" cy="80" r="8" fill="${shade}"/>`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160"><rect width="160" height="160" fill="#f8f6f2"/><circle cx="80" cy="80" r="68" fill="#fff" stroke="#d8d5d1" stroke-width="2"/>${artwork}</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }

  function drawBraceletPreview(context, source, modelSlug, size) {
    // The composition layer keeps case/bezel pixels beneath the opaque case so
    // mixed-metal watches have no seam. Only the clean, exposed links belong in
    // the bracelet option card.
    const top = modelSlug === 'sky-dweller-configurator'
      ? [465, 18, 324, 214]
      : [465, 22, 324, 252];
    const bottom = [465, 1004, 324, 235];
    const cardWidth = size * 0.58;
    const gap = size * 0.04;
    const topHeight = cardWidth * top[3] / top[2];
    const bottomHeight = cardWidth * bottom[3] / bottom[2];
    const startY = (size - topHeight - gap - bottomHeight) / 2;
    const startX = (size - cardWidth) / 2;
    context.drawImage(source, ...top, startX, startY, cardWidth, topHeight);
    context.drawImage(source, ...bottom, startX, startY + topHeight + gap, cardWidth, bottomHeight);
  }

  async function preparePreviews(manifest, assetUrls) {
    await Promise.all(manifest.layers.map(async (option) => {
      if (option.role === 'magnifier') {
        option.previewUrl = simplePreview(option.role, option);
        return;
      }
      if (!['bracelet', 'dial', 'hands', 'seconds'].includes(option.role)) return;
      const source = await imageFor(assetUrls[option.themeAsset]);
      const canvas = document.createElement('canvas');
      const previewSize = 512;
      canvas.width = previewSize;
      canvas.height = previewSize;
      const context = canvas.getContext('2d');
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      if (option.role === 'dial') {
        const previewDial = { x: previewSize / 2, y: previewSize / 2, radius: previewSize * 83 / 180 };
        const previewComposition = { ...manifest.composition, dial: previewDial };
        drawDateWindowBacking(context, previewComposition, option);
        drawDial(context, source, option.crop, previewDial);
        if (option.dateWindow) drawCalendar(context, previewComposition, option, false);
      } else if (option.role === 'bracelet') {
        drawBraceletPreview(context, source, manifest.slug, previewSize);
      } else {
        context.drawImage(source, 0, 0, previewSize, previewSize);
      }
      option.previewUrl = canvas.toDataURL('image/png');
    }));
  }

  window.ReglageReferenceCompositor = { render, preparePreviews };
})();
